// src/hooks/use-internal-time-entries.ts
// VIB-795: Read-only liste over time_entries på et internprosjekt.
// Ansatte ser kun sine egne; admin/superadmin ser alle.
// VIB-1208: utvidet med budget_line_id/task_description + gruppering per
// oppgave.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { parseTaskDescription } from "@/lib/format";

export interface InternalTimeEntry {
  id: string;
  user_id: string;
  user_name: string | null;
  start_date: string | null;
  duration_seconds: number;
  comment: string | null;
  budget_line_id: string | null;
  task_description: string | null;
}

interface Args {
  projectId: string;
  viewerUserId: string | null;
  /** Admin/superadmin ser alle. Andre ser kun viewerUserId. */
  canSeeAll: boolean;
}

export function useInternalTimeEntries({ projectId, viewerUserId, canSeeAll }: Args) {
  return useQuery<InternalTimeEntry[]>({
    queryKey: ["internal-time-entries", projectId, canSeeAll ? "all" : viewerUserId],
    enabled: !!projectId && (canSeeAll || !!viewerUserId),
    queryFn: async () => {
      let q = supabase
        .from("time_entries")
        .select(
          "id, user_id, start_date, duration_seconds, comment, budget_line_id, task_description, users:user_id(full_name)",
        )
        .eq("project_id", projectId)
        .is("deleted_at", null)
        .order("start_date", { ascending: false, nullsFirst: false });
      if (!canSeeAll && viewerUserId) q = q.eq("user_id", viewerUserId);
      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as Array<{
        id: string;
        user_id: string;
        start_date: string | null;
        duration_seconds: number;
        comment: string | null;
        budget_line_id: string | null;
        task_description: string | null;
        users?: { full_name?: string } | null;
      }>).map((e) => ({
        id: e.id,
        user_id: e.user_id,
        user_name: e.users?.full_name ?? null,
        start_date: e.start_date,
        duration_seconds: e.duration_seconds ?? 0,
        comment: e.comment,
        budget_line_id: e.budget_line_id,
        task_description: e.task_description,
      }));
    },
    staleTime: 30 * 1000,
  });
}

export interface InternalTaskGroup {
  /** null = «Uten oppgave»-bucket (VIB-1208: føringer uten budget_line_id — enten
   * eldre data fra før oppgaver fantes, eller entries logget mens flagget var av). */
  budgetLineId: string | null;
  /** Oppgavenavn — utledet fra task_description (parseTaskDescription), eller
   * «Uten oppgave» for null-bucketen. */
  taskName: string;
  entries: InternalTimeEntry[];
  totalSeconds: number;
}

const UNGROUPED_LABEL = "Uten oppgave";

/**
 * VIB-1208: grupperer et internprosjekts time_entries per oppgave
 * (budget_line_id — FK-basert, IKKE string-matching). Entries uten
 * budget_line_id samles i én «Uten oppgave»-bucket, sortert sist. Ingen
 * data tapes — dette er en ren visningsomorganisering.
 *
 * Pure funksjon, eksportert for testbarhet (samme mønster som
 * computeBudgetLineLoggedHours).
 */
export function groupInternalTimeEntriesByTask(entries: InternalTimeEntry[]): InternalTaskGroup[] {
  const groups = new Map<string, InternalTaskGroup>();
  let ungrouped: InternalTaskGroup | null = null;

  for (const entry of entries) {
    if (!entry.budget_line_id) {
      if (!ungrouped) {
        ungrouped = { budgetLineId: null, taskName: UNGROUPED_LABEL, entries: [], totalSeconds: 0 };
      }
      ungrouped.entries.push(entry);
      ungrouped.totalSeconds += entry.duration_seconds;
      continue;
    }
    let group = groups.get(entry.budget_line_id);
    if (!group) {
      const { task } = parseTaskDescription(entry.task_description);
      group = {
        budgetLineId: entry.budget_line_id,
        taskName: task || "—",
        entries: [],
        totalSeconds: 0,
      };
      groups.set(entry.budget_line_id, group);
    }
    group.entries.push(entry);
    group.totalSeconds += entry.duration_seconds;
  }

  const result = Array.from(groups.values());
  if (ungrouped) result.push(ungrouped);
  return result;
}
